import { Tabs } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ReminderList from './ReminderList';
import type { ReminderCreatePayload } from './reminderFormUtils';
import ReminderStatusTable from './ReminderStatusTable';
import { useReminderFirebase } from './services/reminderFirebase';
import type { Reminder, ReminderResponse } from './types/reminder';
import type { Student } from './types/student';
import SentryWrap from 'components/Sentry';

const ReminderTabs = styled(Tabs)`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  touch-action: manipulation;

  .ant-tabs-nav {
    flex-shrink: 0;
    position: relative;
    z-index: 2;
  }

  .ant-tabs-content-holder {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  .ant-tabs-content,
  .ant-tabs-content-top {
    height: 100%;
  }

  .ant-tabs-tabpane {
    height: 100%;
  }
`;

type ReminderWidgetTeacherProps = {
  classId: string;
  teacherId: string;
  students: Student[];
};

export default function ReminderWidgetTeacher({
  classId,
  teacherId,
  students,
}: ReminderWidgetTeacherProps) {
  const {
    subscribeRemindersByClass,
    createReminder,
    updateReminder,
    deleteReminder,
    subscribeResponsesForReminder,
  } = useReminderFirebase();
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [selectedReminderId, setSelectedReminderId] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<string, ReminderResponse>>({});
  const [addFormOpen, setAddFormOpen] = useState(false);
  const [editFormOpen, setEditFormOpen] = useState(false);
  const deletingReminderIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!classId) return () => {};
    const unsub = subscribeRemindersByClass(classId, setReminders);
    return () => unsub();
  }, [classId]);

  useEffect(() => {
    if (!selectedReminderId) {
      setResponses({});
      return () => {};
    }
    const unsub = subscribeResponsesForReminder(selectedReminderId, setResponses);
    return () => unsub();
  }, [selectedReminderId]);

  const handleCreate = async (payload: ReminderCreatePayload) => {
    await createReminder({ ...payload, teacherId });
  };

  const handleEdit = async (
    reminderId: string,
    payload: Partial<Pick<Reminder, 'message' | 'finalReminderTime' | 'softReminderTime' | 'selectedStudents' | 'targetAllStudents'>>
  ) => {
    await updateReminder(reminderId, payload);
  };

  const handleDelete = async (reminderId: string) => {
    await deleteReminder(reminderId);
    if (selectedReminderId === reminderId) setSelectedReminderId(null);
  };

  const isReminderFromToday = (reminder: Reminder) =>
    dayjs(reminder.createdAt || reminder.updatedAt || reminder.finalReminderTime).isSame(dayjs(), 'day');

  useEffect(() => {
    const staleActiveReminders = reminders.filter(
      (reminder) =>
        reminder.status === 'active' &&
        !isReminderFromToday(reminder) &&
        !deletingReminderIdsRef.current.has(reminder.reminderId)
    );
    if (staleActiveReminders.length === 0) return;

    staleActiveReminders.forEach((reminder) => {
      deletingReminderIdsRef.current.add(reminder.reminderId);
    });

    Promise.all(
      staleActiveReminders.map((reminder) =>
        deleteReminder(reminder.reminderId).finally(() => {
          deletingReminderIdsRef.current.delete(reminder.reminderId);
        })
      )
    ).catch(() => {});
  }, [reminders, deleteReminder]);

  useEffect(() => {
    deletingReminderIdsRef.current.forEach((reminderId) => {
      const exists = reminders.some((reminder) => reminder.reminderId === reminderId);
      if (!exists) deletingReminderIdsRef.current.delete(reminderId);
    });
  }, [reminders]);

  const todayReminders = reminders.filter((reminder) => isReminderFromToday(reminder));
  const activeReminders = todayReminders.filter((r) => r.status === 'active');
  const selectedReminder = selectedReminderId
    ? todayReminders.find((r) => r.reminderId === selectedReminderId)
    : null;
  useEffect(() => {
    if (selectedReminderId && !selectedReminder) {
      setSelectedReminderId(null);
    }
  }, [selectedReminderId, selectedReminder]);
  const activeStudents = React.useMemo(
    () => students.filter((student) => student.active !== false),
    [students]
  );

  return (
    <SentryWrap>
      <div
        style={{
          padding: 8,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <ReminderTabs
          items={[
            {
              key: 'list',
              label: 'Reminders',
              children: (
                  <ReminderList
                    reminders={todayReminders}
                    students={activeStudents}
                    classId={classId}
                    teacherId={teacherId}
                    onCreate={handleCreate}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onAddFormOpen={setAddFormOpen}
                    onEditFormOpen={setEditFormOpen}
                  />
              ),
            },
            {
              key: 'status',
              label: 'Status',
              children: (
                <>
                  {!addFormOpen && !editFormOpen && activeReminders.length > 0 && (
                    <div>
                      <strong>View student responses:</strong>
                      <select
                        style={{ marginLeft: 8, maxWidth: '100%' }}
                        value={selectedReminderId ?? ''}
                        onChange={(e) => setSelectedReminderId(e.target.value || null)}
                      >
                        <option value="">Select reminder</option>
                        {activeReminders.map((r) => (
                          <option key={r.reminderId} value={r.reminderId}>
                            {r.message}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  {selectedReminder ? (
                    <ReminderStatusTable
                      studentIds={
                        selectedReminder.targetAllStudents
                          ? activeStudents.map((s) => s.id)
                          : selectedReminder.selectedStudents
                      }
                      students={activeStudents}
                      responses={responses}
                    />
                  ) : (
                    <p style={{ marginTop: 5 }}>Select a reminder to view student status.</p>
                  )}
                </>
              ),
            },
          ]}
        />
      </div>
    </SentryWrap>
  );
}
